import {
  catchErrorInto,
  effect,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { pinyin } from 'pinyin-pro';
import { EMPTY, exhaustMap, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { Member } from '../entities/members';
import type { MemberSearchStore } from '../stores/member-search';

export class MemberSearchService extends Service {
  private readonly ALL_MEMBERS_PAGE_SIZE = 100;
  private readonly allMembersCache = new Map<string, Member[]>();
  private searchController: AbortController | null = null;

  constructor(
    private readonly store: MemberSearchStore,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  readonly PAGE_SIZE = 8;
  readonly searchText$ = new LiveData<string>('');
  readonly isLoading$ = new LiveData(false);
  readonly error$ = new LiveData<any>(null);
  readonly result$ = new LiveData<Member[]>([]);
  readonly hasMore$ = new LiveData(true);

  private sanitizeSearchText(value?: string | null) {
    return (value ?? '').replace(/^[@＠]\s*/, '').trim();
  }

  private normalizeSearchText(value?: string | null) {
    return this.sanitizeSearchText(value).toLowerCase().replace(/\s+/g, '');
  }

  private getPinyinValue(value?: string | null, pattern?: 'first') {
    if (!value?.trim()) {
      return '';
    }
    return this.normalizeSearchText(
      pinyin(value, {
        toneType: 'none',
        ...(pattern ? { pattern } : {}),
      })
    );
  }

  private getMemberSearchScore(member: Member, query: string) {
    const normalizedQuery = this.normalizeSearchText(query);
    if (!normalizedQuery) {
      return Number.POSITIVE_INFINITY;
    }

    const normalizedName = this.normalizeSearchText(member.name);
    const normalizedEmail = this.normalizeSearchText(member.email);
    const fullPinyin = this.getPinyinValue(member.name);
    const initials = this.getPinyinValue(member.name, 'first');

    if (normalizedName.startsWith(normalizedQuery)) {
      return 0;
    }
    if (normalizedName.includes(normalizedQuery)) {
      return 1;
    }
    if (normalizedEmail.startsWith(normalizedQuery)) {
      return 2;
    }
    if (normalizedEmail.includes(normalizedQuery)) {
      return 3;
    }
    if (initials.startsWith(normalizedQuery)) {
      return 4;
    }
    if (fullPinyin.startsWith(normalizedQuery)) {
      return 5;
    }
    if (initials.includes(normalizedQuery)) {
      return 6;
    }
    if (fullPinyin.includes(normalizedQuery)) {
      return 7;
    }

    return Number.POSITIVE_INFINITY;
  }

  private async getAllMembers(signal?: AbortSignal) {
    const workspaceId = this.workspaceService.workspace.id;
    const cached = this.allMembersCache.get(workspaceId);
    if (cached) {
      return cached;
    }

    const members: Member[] = [];
    let skip = 0;

    while (true) {
      const data = await this.store.getMembersByEmailOrName(
        workspaceId,
        undefined,
        skip,
        this.ALL_MEMBERS_PAGE_SIZE,
        signal
      );

      members.push(...data.members);

      if (data.members.length < this.ALL_MEMBERS_PAGE_SIZE) {
        break;
      }

      skip += data.members.length;
    }

    this.allMembersCache.set(workspaceId, members);
    return members;
  }

  readonly loadMore = effect(
    exhaustMap(() => {
      if (!this.hasMore$.value) {
        return EMPTY;
      }
      return fromPromise(async signal => {
        return this.store.getMembersByEmailOrName(
          this.workspaceService.workspace.id,
          this.searchText$.value || undefined,
          this.result$.value.length,
          this.PAGE_SIZE,
          signal
        );
      }).pipe(
        tap(data => {
          this.result$.setValue([...this.result$.value, ...data.members]);
          this.hasMore$.setValue(data.members.length === this.PAGE_SIZE);
        }),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => {
          this.isLoading$.setValue(true);
        }),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  reset() {
    this.searchController?.abort();
    this.searchController = null;
    this.result$.setValue([]);
    this.hasMore$.setValue(true);
    this.searchText$.setValue('');
    this.error$.setValue(null);
    this.loadMore.reset();
  }

  async search(searchText?: string) {
    this.reset();
    const nextSearchText = this.sanitizeSearchText(searchText);
    this.searchText$.setValue(nextSearchText);

    if (!nextSearchText.trim()) {
      this.loadMore();
      return;
    }

    const controller = new AbortController();
    this.searchController = controller;
    this.isLoading$.setValue(true);

    try {
      const members = await this.getAllMembers(controller.signal);
      if (controller.signal.aborted) {
        return;
      }

      const result = members
        .map(member => ({
          member,
          score: this.getMemberSearchScore(member, nextSearchText),
        }))
        .filter(item => Number.isFinite(item.score))
        .sort((left, right) => {
          if (left.score !== right.score) {
            return left.score - right.score;
          }

          return (left.member.name ?? '').localeCompare(right.member.name ?? '');
        })
        .map(item => item.member);

      this.result$.setValue(result);
      this.hasMore$.setValue(false);
      this.error$.setValue(null);
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      this.error$.setValue(error);
    } finally {
      if (this.searchController === controller) {
        this.isLoading$.setValue(false);
        this.searchController = null;
      }
    }
  }
}
